import React, { useCallback, useEffect, useState } from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import { observer } from 'mobx-react';
// import _ from 'lodash';
import axios from 'axios';
import { styled } from '@this/constants/themes';
import Typography from '@material-ui/core/Typography';
import { Box, CircularProgress } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import muiTheme from '@this/components/appoint/mui_theme';
import type { OpenMeetingResponseArgs } from '@this/domain/appoint/open_meeting';
import OpenMeeting, { convertOpenMeetingResponseToArgs } from '@this/domain/appoint/open_meeting';
import type { OpenMeetingBookingResponseArgs } from '@this/domain/appoint/open_meeting_booking';
import OpenMeetingBooking, {
  convertOpenMeetingBookingResponseToArgs
} from '@this/domain/appoint/open_meeting_booking';
// import Participant from '@this/domain/appoint/participant';
import Utils from '@this/domain/appoint/utils';
import type { DestinationInfo } from '../../../shared/fixed_adjustment_dialog/fixed_adjustment_dialog';
import FixedAdjustmentDialog from '../../../shared/fixed_adjustment_dialog/fixed_adjustment_dialog';

const headerHeight = '54px';
const paddingTop = '15px';
const paddingBottom = '15px';

type Props = RouteComponentProps<{ openMeetingKey?: string; key?: string }>;

interface Response {
  open_meeting: OpenMeetingResponseArgs;
  booking: OpenMeetingBookingResponseArgs;
}

// interface ShareUser {
//   name: string;
//   email: string;
// }

interface ParticipantInfo {
  name: string;
  email: string;
}

interface SubmitParams {
  start_time: string;
  end_time: string;
  participants: ParticipantInfo[];
  share_destinations?: DestinationInfo[];
}

const BookingsShow = observer(({ match }: Props) => {
  const [openMeeting, setOpenMeeting] = useState<OpenMeeting | undefined>(undefined);
  const [booking, setBooking] = useState<OpenMeetingBooking | undefined>(undefined);
  const [openMeetingError, setOpenMeetingError] = useState<string | null>(null);
  const [isLoadingOpenMeeting, setIsLoadingOpenMeeting] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  // 意味合いはParticipantと異なるが、要素が同じなので便宜上利用する
  // const [shareUsers, setShareUsers] = useState<Participant[]>([new Participant({ name: '', id: '' })]);

  const openMeetingKey = match.params.openMeetingKey;
  const key = match.params.key;

  const fetchBooking = useCallback(() => {
    setIsLoadingOpenMeeting(true);
    setOpenMeetingError(null);
    axios
      .get<Response>(`/aicalendar/open_meetings/${openMeetingKey}/bookings/${key}.json`)
      .then(res => {
        setOpenMeeting(new OpenMeeting(convertOpenMeetingResponseToArgs(res.data.open_meeting)));
        setBooking(new OpenMeetingBooking(convertOpenMeetingBookingResponseToArgs(res.data.booking)));
      })
      .finally(() => setIsLoadingOpenMeeting(false))
      .catch(() => setOpenMeetingError('通信エラーが発生しました。時間をおいて再度お試しください。'));
  }, [openMeetingKey, key]);

  // マウント時に1回発動
  useEffect(() => {
    fetchBooking();
  }, []);

  // const isInputShareDestination = () => {
  //   const emailRegex = /\S+@\S+\.\S+/;
  //   let result = true;
  //   _.each(shareUsers, user => {
  //     if (user.name.length === 0 || user.id.length === 0 || !emailRegex.test(user.id)) {
  //       result = false;
  //     }
  //   });
  //   return result;
  // };

  // const handleScheduleShare = () => {
  //   Utils.trackEvent('[cal][click] share open meeting booking');
  //   setIsSubmitting(true);
  //   setSubmitError(null);

  //   const params: SubmitParams = {
  //     share_users: shareUsers.map(user => {
  //       return {
  //         name: user.name,
  //         email: user.id
  //       };
  //     })
  //   };
  //   axios
  //     .put(`/aicalendar/open_meetings/${openMeetingKey}/bookings/${key}.json`, params)
  //     .then(() => {})
  //     .finally(() => setIsSubmitting(false))
  //     .catch(() => setSubmitError('通信エラーが発生しました。時間をおいて再度お試しください。'));
  // };

  const handleScheduleShare = async (share_destinations: DestinationInfo[]) => {
    Utils.trackEvent('[cal][click] share open_meeting_booking');
    setIsSubmitting(true);
    setOpenMeetingError(null);
    let participants = booking?.participants.map(p => ({ name: p.name, email: p.email || p.id })) || [];
    participants = participants.concat(share_destinations);
    try {
      // todo: share_destinationsに複数格納可とする
      const params: SubmitParams = {
        start_time: '',
        end_time: '',
        participants
      };
      await axios.put<Response>(`/aicalendar/open_meetings/${openMeetingKey}/bookings/${key}.json`, params);
    } catch (e) {
      setOpenMeetingError('通信エラーが発生しました。時間をおいて再度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
    setIsSubmitted(true);
  };

  return (
    <Box
      bgcolor={muiTheme.palette.grey[200]}
      height={`calc(100vh - ${headerHeight} - ${paddingTop} - ${paddingBottom})`}
      padding={`${paddingTop} 15px ${paddingBottom}`}
    >
      {isLoadingOpenMeeting ? (
        <Box textAlign="center">
          <CircularProgress />
        </Box>
      ) : isSubmitted ? (
        <SubmittedWrap>
          <Paper style={{ position: 'relative', margin: 'auto', maxWidth: '280px', padding: '30px' }}>
            <SubmittedHead>
              <SubmittedHeadIcon>
                <ThumbUpIcon fontSize="large" />
              </SubmittedHeadIcon>
              <SubmittedTitle style={{ margin: '15px 0 10px' }}>予定の共有が完了しました</SubmittedTitle>
            </SubmittedHead>
            <Box>
              <Box style={{ marginTop: '20px', textAlign: 'center' }}>
                <Button variant="outlined" color="primary" style={{ fontSize: '11px' }} href="/aicalendar">
                  カレンダーに戻る
                </Button>
              </Box>
            </Box>
          </Paper>
        </SubmittedWrap>
      ) : (
        openMeeting &&
        booking && (
          <FixedAdjustmentDialog
            show
            title={openMeeting.title}
            startTime={booking.startTime}
            endTime={booking.endTime}
            meetingType={openMeeting.meetingType || ''}
            onlineMeetingName={openMeeting.onlineMeetingName() || ''}
            onlineMeetingUrl={openMeeting.onlineMeetingUrl() || ''}
            placeName={openMeeting.place || openMeeting.placeAddress}
            description={openMeeting.description || ''}
            onSubmit={handleScheduleShare}
            isSubmitting={isSubmitting}
            errorMessage={openMeetingError}
          />
        )
      )}
    </Box>
  );
});

const SubmittedWrap = styled(Box)`
  position: relative;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const SubmittedHead = styled(Box)`
  margin-bottom: 25px;
  text-align: center;
`;

const SubmittedHeadIcon = styled.div`
  margin-bottom: 5px;
  color: ${muiTheme.palette.primary.main};
`;

const SubmittedTitle = styled(Typography)`
  font-size: 20px;
  font-weight: 700;
`;

export default BookingsShow;
